// v49.24 — Hybrid view-as override.
// REWRITTEN 2026-08-24 — the state now lives in the acting-context store
// (lib/context/actingSlice.ts) rather than in this file's own localStorage key.
//
// WHY THE SHAPE IS UNCHANGED
//
// `useEffectiveRole()` is read by the sidebar, the top bar and the navigation
// filter. Keeping its signature means none of them need rewriting — the body
// changed, the contract did not. That was the point of doing it this way.
//
// WHAT WAS ACTUALLY WRONG WITH THE OLD VERSION
//
// Nothing about how it stored the choice. The flaw was that the choice never
// left the browser: not one API request carried it, so every query answered
// "everything for this company" on both sides of the marketplace at once. A
// hybrid firm in Supplying mode was still shown the requirements it had posted
// itself. The interface knew which hat the user was wearing; the data layer had
// never been told.
//
// The store fixes that by sending the mode as a header on every request, where
// the server validates it. See Product-KT/08-Decision-Acting-Context.md.

import { useShellUser } from "@/features/auth/AuthContext";
import { useActingContext, useSetActingTier } from "@/lib/context/useActingContext";
import type { Role } from "@/lib/api/types";

export type RoleOverride = "coordinator" | "partner" | null;

/**
 * The role the UI should treat the user as.
 *
 * For a hybrid company this is the acting mode — the side the user has chosen.
 * For a coordinator or partner company the mode IS the company's kind, so the
 * answer is the same either way.
 *
 * One behaviour change worth naming: the old version returned the literal
 * string `"hybrid"` when a hybrid user had made no choice. `"hybrid"` is not a
 * side of the marketplace, so nothing downstream could filter on it — which is
 * part of why the mode never reached the data layer. It now resolves to a real
 * side, defaulting to `coordinator`.
 */
export function useEffectiveRole(): Role | null {
  const user = useShellUser();
  const { tier, hydrated } = useActingContext();

  if (!user) return null;
  /* Before hydration, fall back to the shell user's own value so the chrome
     renders something stable rather than flickering. */
  if (!hydrated || !tier) return user.role;

  /* THE TRANSLATION LAYER, and it is temporary.
     `Role` is the old three-value axis and a lot of screens still branch on
     it. tier1 buys and mep supplies, so those map cleanly. The other three
     tiers have no position in the labour transaction at all — an owner buys
     design, a consultant sells it, an operative sells their own hours — so
     there is no honest `Role` for them and the profile's value stands.
     Those tiers' screens do not branch on Role; they read the tier. */
  if (tier === "tier1") return "coordinator";
  if (tier === "mep") return "partner";
  return user.role;
}

/**
 * Reader/writer for the Buying/Supplying toggle.
 *
 * Now backed by tiers. The toggle appears when the ACTIVE COMPANY HOLDS BOTH
 * `tier1` and `mep` — which is precisely what `hybrid` used to mean, expressed
 * as two grants instead of a third company kind.
 *
 * `null` when the company holds only one of them: there is nothing to toggle.
 */
export function useRoleOverride(): [RoleOverride, (next: RoleOverride) => void] {
  const { tier, companyId, actable } = useActingContext();
  const setTier = useSetActingTier();

  const here = actable.filter((a) => a.company_id === companyId).map((a) => a.tier_code);
  const bothSides = here.includes("tier1") && here.includes("mep");

  const current: RoleOverride = bothSides
    ? (tier === "mep" ? "partner" : tier === "tier1" ? "coordinator" : null)
    : null;

  return [
    current,
    (next) => {
      if (!bothSides) return;
      if (next === "coordinator") setTier("tier1", companyId);
      else if (next === "partner") setTier("mep", companyId);
      /* `null` used to mean "clear the override". There is no such state — a
         company always acts as some tier — so clearing is a no-op. */
    },
  ];
}

/**
 * @deprecated The acting context is not readable outside React any more —
 * use `currentActingContext()` from lib/context/store if you need it in a
 * non-component, or `useActingContext()` inside one.
 *
 * Kept as a no-op returning null so any straggling import still compiles while
 * the last call sites are found.
 */
export function readOverride(): RoleOverride {
  return null;
}
